import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TableTab from "../../../components/tabs/table-tab";
import { REPORTS_FIRST_IN_LAST_OUT } from "../../../constants/menu-types";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { DEF_PG_SIZE_REPORTS } from '../../../constants/maximum-page-size';
import FirstInLastOutReportColumn from "../../../models/ColumnDefinitions/FirstInLastOutReportColumn";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import EnrolmentSearch from "../../../models/search/EnrolmentSearch";
import FirstInLastOutReportSearch from "../../../models/search/FirstInLastOutReportSearch";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getFirstInLastOutReportByReportType } from "../../../services/reportService";
import { getEnrolmentNamesWithPagination } from "../../../services/enrolmentService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { getDayOfWeekByDay } from "../../../services/utilities/dateFormatting";
import { buildFirstInLastOutWeekDaysColumns, getDayColumnId } from "../../../utilities/DateUtils";

type Props = {
    reportType: number;
    reportId: string;
}

const FirstInLastOutReport: FC<Props> = ({ reportType, reportId }) => {
    const { translate } = useLocale();
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const getFirstInLastOutDayColumns = buildFirstInLastOutWeekDaysColumns();
    const generatedFirstInLastOutColumns = getFirstInLastOutDayColumns.map(item => {
        const day = getDayOfWeekByDay(item.day);
        return {
            heading: translate.byKey(day),
            accessor: item.accessor,
            isNumeric: false,
            id: parseInt(item.day),
            active: true,
            columnId: getDayColumnId(day)
        }
    });
    const [dynamicColumns, setDynamicColumns] = useState<any>([...FirstInLastOutReportColumn, ...generatedFirstInLastOutColumns]);
    
    useEffect(() => {
        setLookup([
            {
                name: "master.company.id",
                values: [],
                dataMethod: getCompanyNamesWithPagination,
                searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: "master.department.id",
                values: [],
                dataMethod: getDepartmentNamesWithPagination,
                searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: "master.profile.id",
                values: [],
                dataMethod: getCategoryNameWithPagination,
                searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: "master.id",
                values: [],
                dataMethod: getEnrolmentNamesWithPagination,
                searchAccessor: EnrolmentSearch.find(column => column.accessor === "name")?.searchOnName
            }
        ]);
    }, [])

    useEffect(() => {
        dataRefresh(getDataMethod);
    },[reportType])

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_FIRST_IN_LAST_OUT;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, [], reportId) => void) => {
        setDetails(userLoginId, currentMenuDetail, [], reportId);
    }
    // <-- end context

    const getDataMethod = async (listParameters?: any) => {
        try {
            return getFirstInLastOutReportByReportType(reportType, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return (<TableContextProvider
        maxPageSize={DEF_PG_SIZE_REPORTS}
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={dynamicColumns}
        searchDescriptor={FirstInLastOutReportSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}>
        <TableTab
            title={''}
            reportTitle={(reportType === 0 ? translate.byKey(TranslationKeys.firstInReport) : translate.byKey(TranslationKeys.lastOutReport))}
            selectRows={false}
            cyName="firstInLastOut" />
    </TableContextProvider >
    );
};

export default FirstInLastOutReport;
